// src/pages/user/UserListPage.tsx
// Admin user table with status badges, impersonate, reset MFA.

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useUser, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardContent, ErrorAlert,
} from '@react/components';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import { formatUserStatus, canImpersonateUser, canResetMFA } from '../../features/user/logic';
import type { User } from '@core/types/user';

export const UserListPage = observer(() => {
  const userStore = useUser();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');

  useEffect(() => {
    userStore.ListUsers({ limit: 20 } as never);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { q: search } : {});
    userStore.ListUsers({ limit: 20, search } as never);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('user.deleteConfirm'))) return;
    try {
      await userStore.DeleteUser(id);
    } catch {
      // handled by store
    }
  };

  const users = userStore.items as User[];

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('user.list')}</h1>

      {userStore.error ? <ErrorAlert error={userStore.error} dismissible onDismiss={() => userStore.clearError()} /> : null}

      <Card>
        <CardHeader>
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardHeader>
        <CardContent>
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('user.identity')}</TableHead>
                <TableHead>{t('auth.username')}</TableHead>
                <TableHead>{t('user.phone')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead>{t('user.emailVerified')}</TableHead>
                <TableHead>{t('user.phoneVerified')}</TableHead>
                <TableHead>{t('user.twoFactor')}</TableHead>
                <TableHead>{t('user.lastSeen')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 && !userStore.isLoading && <TableEmpty colSpan={9} message={t('user.noItems')} />}
              {users.map((user) => {
                const status = formatUserStatus(user.status ?? '');
                return (
                  <TableRow key={user.entity?.id}>
                    <TableCell>
                      <Link to={`/users/${user.entity?.id}`} className="text-sm font-medium text-primary hover:text-primary/80">{user.email}</Link>
                    </TableCell>
                    <TableCell className="text-sm">{user.username}</TableCell>
                    <TableCell className="text-sm">{user.phone || '-'}</TableCell>
                    <TableCell><StatusBadge variant={status.variant} label={t(status.labelKey)} size="sm" /></TableCell>
                    <TableCell><span className={user.email_verified ? 'text-green-600' : 'text-muted-foreground'}>{user.email_verified ? t('common.yes') : t('common.no')}</span></TableCell>
                    <TableCell><span className={user.phone_verified ? 'text-green-600' : 'text-muted-foreground'}>{user.phone_verified ? t('common.yes') : t('common.no')}</span></TableCell>
                    <TableCell><span className={user.two_factor_enabled ? 'text-green-600' : 'text-muted-foreground'}>{user.two_factor_enabled ? t('common.yes') : t('common.no')}</span></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{user.last_seen_at ?? '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Link to={`/users/${user.entity?.id}`}><Button variant="ghost" size="sm">{t('common.open')}</Button></Link>
                        <Link to={`/users/${user.entity?.id}/edit`}><Button variant="ghost" size="sm">{t('common.edit')}</Button></Link>
                        {canImpersonateUser(user) && <Button variant="ghost" size="sm">{t('user.impersonate')}</Button>}
                        {canResetMFA(user) && <Button variant="ghost" size="sm">{t('user.resetMFA')}</Button>}
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(user.entity?.id ?? '')}>{t('common.delete')}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {userStore.hasMore && (
            <div className="mt-4 flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('common.showingItems', { count: users.length })}</span>
              <Button variant="secondary" size="sm" onClick={() => userStore.ListUsers({ limit: 20, cursor: userStore.pageInfo?.next_cursor } as never)} isLoading={userStore.isLoading}>{t('common.next')}</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default UserListPage;
